<script setup lang="ts">

import resolveConfig from 'tailwindcss/resolveConfig';

import tailwindConfig from '~/packages/base/tailwind.config';

type Image = {
    width: number;
    height: number;
    url: string;
    alt: string;
    description?: string;
}

defineProps<{
    reverseOrder?: boolean;
    link?: string;
    imageSrc: string;
    imageAlt: string;
    imageWidth?: string;
    imageHeight?: string;
    showImagesOnMobile?: boolean;
    images: Image[];
}>();

const fullConfig = resolveConfig(tailwindConfig as any);
const isLargeDevice = useMatchMedia(
    `(min-width: ${fullConfig.theme.screens.lg})`,
);

const isXLargeDevice = useMatchMedia(
    `(min-width: ${fullConfig.theme.screens.xl})`,
);
</script>

<template>
    <div
        class="flex w-full flex-col items-center justify-between gap-6 md:gap-[4.5rem]"
        :class="[reverseOrder ? 'md:flex-row-reverse' : 'md:flex-row']"
    >
        <CommBaseModal
            v-if="images?.length === 1"
            trigger-classes="lg:flex-shrink-0"
            additional-classes="p-5"
        >
            <template #trigger>
                <NuxtImg
                    loading="lazy"
                    format="webp"
                    :width="images[0].width ?? '550'"
                    :height="images[0].height ?? '310'"
                    :alt="images[0].alt"
                    :src="images[0].url"
                    decoding="auto"
                    background="white"
                    :class="`${showImagesOnMobile ? 'block' : 'hidden'} cursor-zoom-in w-full rounded-lg border border-gray-100 lg:block`"
                />
            </template>
            <NuxtImg
                loading="lazy"
                format="webp"
                fit="inside"
                quality="100"
                :alt="images[0].alt"
                :src="images[0].url"
                background="white"
                decoding="auto"
            />
        </CommBaseModal>
        <ClientOnly  v-else-if="images?.length > 1">
            <CommBaseCarouselVertical
                v-if="isLargeDevice"
                :images
                has-image-preview
                loading="eager"
                has-arrows
                :height="isXLargeDevice ? '23rem' : '18rem'"
                class="!w-auto"
            />
            <CommBaseCarouselHorizontal
                v-else
                :images
                has-image-preview
                has-dots
                has-aspect-video
                :width="80"
            />
        </ClientOnly>
        <div class="flex grow flex-col gap-4">
            <BaseHeading as="h3" variant="lg-500">
                <slot name="headline" />
            </BaseHeading>
            <BaseText v-if="$slots.description">
                <slot name="description" />
            </BaseText>
            <JamBaseButton
                v-if="link"
                variant="outline"
                class="w-full lg:w-fit"
                :to="link"
            >
                Mehr erfahren
            </JamBaseButton>
            <div class="xl:w-4/5">
                <slot />
            </div>
        </div>
    </div>
</template>
